import axios, { AxiosInstance } from 'axios';

export interface CodeValidationResponse {
  valid: boolean;
  code: AccessCodeInfo;
}

export interface AccessCodeInfo {
  code: string;
  status: 'unused' | 'activated' | 'used' | 'expired';
  is_organizer: boolean;
  team_number: number;
  activated_at: string | null;
  expires_at: string | null;
  package_name: string;
  package_type: 'solo' | 'group';
  template_name: string;
  template_location: string;
  validity_days: number;
  max_duration_minutes: number | null;
  time_remaining_seconds: number | null;
}

export interface SessionInfo {
  id: number;
  session_type: 'solo' | 'group';
  status: 'waiting' | 'ready' | 'active' | 'finished';
  teams_required: number;
  teams_logged_in: number;
  actual_start_time: string | null;
  auto_finish_at: string | null;
  template_name: string;
  template_id: number;
  time_remaining_seconds: number;
}

export interface TeamRegistrationResponse {
  success: boolean;
  team_id: number;
  team_name: string;
  team_password: string;
  session: SessionInfo;
  code: AccessCodeInfo;
  can_start: boolean;
}

export interface GameStartResponse {
  success: boolean;
  session: SessionInfo;
  message: string;
}

export interface CodeStatusResponse {
  code: AccessCodeInfo;
  has_team: boolean;
  team?: {
    id: number;
    name: string;
  };
  session?: SessionInfo;
}

// Hints API types
export interface HintInfo {
  id: number;
  level: number;
  level_display: string;
  penalty_minutes: number;
  is_used: boolean;
  hint_text: string | null;
}

export interface HintsResponse {
  hints_enabled: boolean;
  tile_number?: number;
  hints: HintInfo[];
  max_hints?: number;
  used_count?: number;
}

export interface UseHintResponse {
  success: boolean;
  hint_text: string;
  penalty_minutes: number;
  level: number;
  message: string;
}

export interface TeamPenaltyResponse {
  total_penalty_minutes: number;
  hints_used: number;
}

// Lobby API types (Group mode)
export interface LobbyTeam {
  team_number: number;
  team_name: string | null;
  is_organizer: boolean;
  is_registered: boolean;
}

export interface LobbyResponse {
  session: SessionInfo;
  teams: LobbyTeam[];
  all_teams_ready: boolean;
  can_start: boolean;
  organizer_team_id: number | null;
}

// Ranking API types (Group mode - Live Ranking)
export interface RankingEntry {
  team_id: number;
  team_name: string;
  team_number: number;
  is_organizer: boolean;
  correct_answers: number;
  total_tiles: number;
  penalty_minutes: number;
  is_finished: boolean;
  game_time: string | null;
  game_time_seconds: number | null;
  is_current_team: boolean;
  position: number;
}

export interface RankingResponse {
  session_id: number;
  session_status: string;
  is_frozen: boolean;
  total_tiles: number;
  ranking: RankingEntry[];
  current_team_id: number | null;
}

// Game Finish API types
export interface FinishGameResponse {
  success: boolean;
  message: string;
  stopped_clock: boolean;
  total_time?: string;
  game_time?: string;
  penalty_minutes?: number;
  correct_answers?: number;
  total_tiles?: number;
  position?: number;
  percentile?: number;
}

// Team Result API types
export interface TeamResultResponse {
  team_name: string;
  session_type: 'solo' | 'group';
  total_time: string;
  game_time: string;
  penalty_minutes: number;
  correct_answers: number;
  total_tiles: number;
  completed_all: boolean;
  stopped_clock: boolean;
  position_overall: number;
  position_in_type: number;
  percentile_overall: number;
  percentile_in_type: number;
  leaderboard_url: string;
}

// Progress Milestone API types
export interface MilestoneInfo {
  percentage: number;
  message: string;
  actual_percentage: number;
}

export interface MilestoneResponse {
  milestone: MilestoneInfo | null;
  correct_answers: number;
  total_tiles: number;
  percentage: number;
}

// Global Leaderboard API types
export interface LeaderboardEntry {
  position: number;
  id: number;
  team_name: string;
  session_type: 'solo' | 'group';
  total_time: string;
  total_time_seconds: number;
  game_time: string;
  penalty_minutes: number;
  correct_answers: number;
  total_tiles: number;
  completed_all: boolean;
  stopped_clock: boolean;
  date: string;
  is_current_team: boolean;
}

export interface CurrentTeamEntry {
  position: number;
  id: number;
  team_name: string;
  total_time: string;
  percentile: number;
}

export interface LeaderboardResponse {
  template_id: number;
  template_name: string;
  total_entries: number;
  limit: number;
  offset: number;
  session_type_filter: string;
  period_filter: string;
  ranking: LeaderboardEntry[];
  current_team: CurrentTeamEntry | null;
}

// Comparison Stats API types (Motivational popups)
export interface ComparisonStatsResponse {
  insufficient_data: boolean;
  total_teams: number;
  at_current_time: {
    avg_correct: number;
    best_correct: number;
    your_correct: number;
  } | null;
  overall: {
    best_time_formatted: string;
    avg_time_formatted: string;
  } | null;
  estimated_percentile: number | null;
  pace_message: 'behind' | 'on_track' | 'ahead' | null;
}

// Public templates list (for Solari board)
export interface GameTemplateInfo {
  id: number;
  name: string;
  location: string;
}

export interface TemplatesResponse {
  templates: GameTemplateInfo[];
}

class SelfServiceApi {
  private api: AxiosInstance;

  constructor() {
    const envUrl = import.meta.env?.VITE_API_URL;
    const baseURL = envUrl !== undefined ? envUrl : (import.meta.env?.DEV ? 'http://127.0.0.1:8000' : '');

    this.api = axios.create({
      baseURL: baseURL,
      timeout: 15000,
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  private getCsrfToken(): string | null {
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'csrftoken') {
        return value;
      }
    }
    return null;
  }

  async validateCode(code: string): Promise<CodeValidationResponse> {
    const response = await this.api.post<CodeValidationResponse>(
      '/api/selfservice/validate-code/',
      { code }
    );
    return response.data;
  }

  async registerTeam(code: string, teamName: string, phoneNumber?: string): Promise<TeamRegistrationResponse> {
    const csrfToken = this.getCsrfToken();
    const response = await this.api.post<TeamRegistrationResponse>(
      '/api/selfservice/register/',
      { code, team_name: teamName, phone_number: phoneNumber || '' },
      { headers: csrfToken ? { 'X-CSRFToken': csrfToken } : {} }
    );
    return response.data;
  }

  async startGame(sessionId: number): Promise<GameStartResponse> {
    const csrfToken = this.getCsrfToken();
    const response = await this.api.post<GameStartResponse>(
      '/api/selfservice/start/',
      { session_id: sessionId },
      { headers: csrfToken ? { 'X-CSRFToken': csrfToken } : {} }
    );
    return response.data;
  }

  async getCodeStatus(code: string): Promise<CodeStatusResponse> {
    const response = await this.api.get<CodeStatusResponse>(
      `/api/selfservice/code/${code}/`
    );
    return response.data;
  }

  async getSessionStatus(sessionId: number): Promise<{ session: SessionInfo }> {
    const response = await this.api.get<{ session: SessionInfo }>(
      `/api/selfservice/session/${sessionId}/`
    );
    return response.data;
  }

  // =============================================================================
  // HINTS API
  // =============================================================================

  async getHints(sessionId: number, tileNumber: number, code: string): Promise<HintsResponse> {
    const response = await this.api.get<HintsResponse>(
      `/api/selfservice/session/${sessionId}/tile/${tileNumber}/hints/`,
      { params: { code } }
    );
    return response.data;
  }

  async useHint(sessionId: number, tileNumber: number, code: string, hintLevel: number): Promise<UseHintResponse> {
    const csrfToken = this.getCsrfToken();
    const response = await this.api.post<UseHintResponse>(
      `/api/selfservice/session/${sessionId}/tile/${tileNumber}/use-hint/`,
      { code, hint_level: hintLevel },
      { headers: csrfToken ? { 'X-CSRFToken': csrfToken } : {} }
    );
    return response.data;
  }

  async getTeamPenalty(sessionId: number, code: string): Promise<TeamPenaltyResponse> {
    const response = await this.api.get<TeamPenaltyResponse>(
      `/api/selfservice/session/${sessionId}/team-penalty/`,
      { params: { code } }
    );
    return response.data;
  }

  // =============================================================================
  // LOBBY API (Group mode)
  // =============================================================================

  async getLobby(sessionId: number): Promise<LobbyResponse> {
    const response = await this.api.get<LobbyResponse>(
      `/api/selfservice/session/${sessionId}/lobby/`
    );
    return response.data;
  }

  // =============================================================================
  // RANKING API (Group mode - Live Ranking)
  // =============================================================================

  async getRanking(sessionId: number, code?: string): Promise<RankingResponse> {
    const response = await this.api.get<RankingResponse>(
      `/api/selfservice/session/${sessionId}/ranking/`,
      { params: code ? { code } : {} }
    );
    return response.data;
  }

  // =============================================================================
  // GAME COMPLETION API
  // =============================================================================

  async finishGame(sessionId: number, code: string, stopCode?: string): Promise<FinishGameResponse> {
    const csrfToken = this.getCsrfToken();
    const response = await this.api.post<FinishGameResponse>(
      `/api/selfservice/session/${sessionId}/finish/`,
      { code, stop_code: stopCode || '' },
      { headers: csrfToken ? { 'X-CSRFToken': csrfToken } : {} }
    );
    return response.data;
  }

  async getTeamResult(sessionId: number, code: string): Promise<TeamResultResponse> {
    const response = await this.api.get<TeamResultResponse>(
      `/api/selfservice/session/${sessionId}/result/`,
      { params: { code } }
    );
    return response.data;
  }

  async getMilestone(sessionId: number, code: string): Promise<MilestoneResponse> {
    const response = await this.api.get<MilestoneResponse>(
      `/api/selfservice/session/${sessionId}/milestone/`,
      { params: { code } }
    );
    return response.data;
  }

  // =============================================================================
  // GLOBAL LEADERBOARD API
  // =============================================================================

  async getLeaderboard(
    templateId: number,
    options?: {
      sessionType?: 'solo' | 'group' | 'all';
      period?: 'week' | 'month' | 'year' | 'all';
      limit?: number;
      offset?: number;
      code?: string;
    }
  ): Promise<LeaderboardResponse> {
    const params: Record<string, string | number> = {};
    if (options?.sessionType) params.session_type = options.sessionType;
    if (options?.period) params.period = options.period;
    if (options?.limit) params.limit = options.limit;
    if (options?.offset) params.offset = options.offset;
    if (options?.code) params.code = options.code;

    const response = await this.api.get<LeaderboardResponse>(
      `/api/selfservice/leaderboard/${templateId}/`,
      { params }
    );
    return response.data;
  }

  // =============================================================================
  // COMPARISON STATS API (Motivational popups)
  // =============================================================================

  // =============================================================================
  // PUBLIC TEMPLATES LIST (Solari board)
  // =============================================================================

  async getTemplates(): Promise<TemplatesResponse> {
    const response = await this.api.get<TemplatesResponse>(
      '/api/selfservice/templates/'
    );
    return response.data;
  }

  async getComparisonStats(
    sessionId: number,
    code: string,
    elapsedSeconds: number,
    correctAnswers: number
  ): Promise<ComparisonStatsResponse> {
    const response = await this.api.get<ComparisonStatsResponse>(
      `/api/selfservice/session/${sessionId}/comparison-stats/`,
      { params: { code, elapsed_seconds: elapsedSeconds, correct_answers: correctAnswers } }
    );
    return response.data;
  }
}

export const selfServiceApi = new SelfServiceApi();
